import { supabase } from "@/integrations/supabase/client";

interface InviteParticipantParams {
  meetingId: string;
  email: string;
  recipientName?: string;
}

/**
 * Invite a participant to a meeting
 * This creates an invitation record that will trigger a reminder email
 */
export const inviteParticipant = async ({
  meetingId,
  email,
  recipientName,
}: InviteParticipantParams) => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error("You must be logged in to invite participants");
  }

  const { data, error } = await supabase
    .from("meeting_invitations")
    .insert({
      meeting_id: meetingId,
      email: email.toLowerCase().trim(),
      recipient_name: recipientName,
      invited_by: user.id,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
};

/**
 * Invite multiple participants to a meeting
 */
export const inviteMultipleParticipants = async (
  meetingId: string,
  participants: Array<{ email: string; name?: string }>
) => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error("You must be logged in to invite participants");
  }

  const invitations = participants.map((p) => ({
    meeting_id: meetingId,
    email: p.email.toLowerCase().trim(),
    recipient_name: p.name,
    invited_by: user.id,
  }));

  const { data, error } = await supabase
    .from("meeting_invitations")
    .insert(invitations)
    .select();

  if (error) {
    throw error;
  }

  return data;
};

/**
 * Get all invitations for a meeting
 */
export const getMeetingInvitations = async (meetingId: string) => {
  const { data, error } = await supabase
    .from("meeting_invitations")
    .select("*")
    .eq("meeting_id", meetingId)
    .order("invited_at", { ascending: false });

  if (error) {
    throw error;
  }

  return data;
};

/**
 * Remove an invitation
 */
export const removeInvitation = async (invitationId: string) => {
  const { error } = await supabase
    .from("meeting_invitations")
    .delete()
    .eq("id", invitationId);

  if (error) {
    throw error;
  }
};

/**
 * Send immediate invitation email (separate from reminder)
 */
export const sendMeetingInvitation = async (params: {
  recipientEmail: string;
  recipientName?: string;
  meetingTitle: string;
  meetingCode: string;
  hostName: string;
  scheduledDate: string;
  scheduledTime: string;
  description?: string;
  hasPassword: boolean;
}) => {
  const { data, error } = await supabase.functions.invoke("send-meeting-invitation", {
    body: {
      ...params,
      joinUrl: `${window.location.origin}/join/${params.meetingCode}`,
      siteUrl: window.location.origin,
    },
  });

  if (error) {
    throw error;
  }

  return data;
};
